import { __decorate } from "tslib";
import React, { cloneElement, Component, isValidElement, useState } from 'react';
import { observer } from 'mobx-react';
import { action, isArrayLike, observable } from 'mobx';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import isNumber from 'lodash/isNumber';
import { pxToRem } from 'choerodon-ui/lib/_util/UnitConvertor';
import Icon from 'choerodon-ui/lib/icon';
import { ProgressStatus } from 'choerodon-ui/lib/progress/enum';
import noop from 'lodash/noop';
import { TableButtonType, TableQueryBarType } from '../enum';
import TableButtons from './TableButtons';
import Table from '../Table';
import Button from '../../button/Button';
import Radio from '../../radio';
import { ButtonColor, ButtonType } from '../../button/enum';
import { DataSetExportStatus, DataSetStatus, FieldType } from '../../data-set/enum';
import { $l } from '../../locale-context';
import TableContext from '../TableContext';
import autobind from '../../_util/autobind';
import DataSet from '../../data-set';
import Modal from '../../modal';
import Progress from '../../progress';
import Column from '../Column';
import { getEditorByField, getPlaceholderByField } from '../utils';
import TableToolBar from './TableToolBar';
import TableFilterBar from './TableFilterBar';
import TableAdvancedQueryBar from './TableAdvancedQueryBar';
import TableProfessionalBar from './TableProfessionalBar';
import TableComboBar from './TableComboBar';
import TableDynamicFilterBar from './TableDynamicFilterBar';
import { exportExcel, findBindFieldBy } from '../../data-set/utils';
import Dropdown from '../../dropdown/Dropdown';
import Menu from '../../menu';
import TextField from '../../text-field';
const ExportBody = observer((props) => {
    const { dataSet, prefixCls } = props;
    let exportMessage = $l('Table', 'export_ing');
    let exportProgress = {
        percent: 1,
        status: ProgressStatus.active,
    };
    switch (dataSet.exportStatus) {
        case DataSetExportStatus.start:
            exportProgress = {
                percent: 1,
                status: ProgressStatus.active,
            };
            break;
        case DataSetExportStatus.exporting:
            exportProgress = {
                percent: dataSet.exportProgress,
                status: ProgressStatus.active,
            };
            break;
        case DataSetExportStatus.progressing:
            exportProgress = {
                percent: dataSet.exportProgress,
                status: ProgressStatus.active,
            };
            break;
        case DataSetExportStatus.failed:
            exportMessage = $l('Table', 'export_failed');
            exportProgress = {
                percent: 50,
                status: ProgressStatus.exception,
            };
            break;
        case DataSetExportStatus.success:
            exportMessage = $l('Table', 'export_success');
            exportProgress = {
                percent: 100,
                status: ProgressStatus.success,
            };
            break;
        default:
            break;
    }
    return (React.createElement("div", { className: `${prefixCls}-export-progress-body` },
        React.createElement("span", null, exportMessage),
        React.createElement(Progress, Object.assign({}, exportProgress))));
});
const ExportFooter = observer((props) => {
    const { dataSet, prefixCls, exportButton } = props;
    const [username, setUsername] = useState(dataSet.name || $l('Table', 'default_export'));
    const handleClick = () => {
        exportButton(dataSet.exportStatus, username);
    };
    const [messageTimeout, setMessageTimeout] = useState(undefined);
    React.useEffect(() => {
        let currentTimeout = null;
        currentTimeout = setTimeout(() => {
            if (dataSet && dataSet.exportStatus !== DataSetExportStatus.success && dataSet.exportStatus !== DataSetExportStatus.failed) {
                setMessageTimeout($l('Table', 'export_waiting'));
            }
        }, 5000);
        return () => {
            if (currentTimeout) {
                clearTimeout(currentTimeout);
            }
        };
    }, []);
    return (React.createElement("div", { className: `${prefixCls}-export-progress-footer` },
        dataSet.exportStatus === DataSetExportStatus.failed && React.createElement(React.Fragment, null,
            React.createElement("span", null, $l('Table', 'export_break')),
            React.createElement(Button, { onClick: handleClick }, $l('Table', 'retry_button'))),
        dataSet.exportStatus === DataSetExportStatus.success && React.createElement(React.Fragment, null,
            React.createElement("div", null,
                React.createElement("span", null, `${$l('Table', 'file_name')}:`),
                React.createElement(TextField, { value: username, onChange: (value) => {
                        setUsername(value);
                    } })),
            React.createElement(Button, { color: ButtonColor.primary, onClick: handleClick }, $l('Table', 'download_button'))),
        dataSet.exportStatus !== DataSetExportStatus.success &&
            dataSet.exportStatus !== DataSetExportStatus.failed &&
            React.createElement(React.Fragment, null,
                React.createElement("span", null, messageTimeout || $l('Table', 'export_operating')),
                React.createElement(Button, { color: ButtonColor.gray, onClick: handleClick }, $l('Table', 'cancel_button')))));
});
let TableQueryBar = class TableQueryBar extends Component {
    constructor() {
        super(...arguments);
        /**
         * 点击汇总条展开收起
         * @param summary
         */
        this.openMore = (summary) => {
            if (this.moreSummary && this.moreSummary.length) {
                this.moreSummary = [];
            }
            else {
                this.moreSummary = this.renderSummary(summary);
            }
            return this.moreSummary;
        };
    }
    static get contextType() {
        return TableContext;
    }
    get showQueryBar() {
        const { props: { showQueryBar }, context: { tableStore: { queryBar }, }, } = this;
        return showQueryBar !== false && queryBar !== TableQueryBarType.none;
    }
    componentWillUnmount() {
        if (this.exportModal) {
            this.exportModal.close(true);
        }
    }
    handleButtonCreate() {
        const { dataSet, } = this.context;
        dataSet.create({}, 0);
    }
    handleButtonSubmit() {
        const { dataSet, } = this.context;
        return dataSet.submit();
    }
    handleButtonDelete() {
        const { dataSet, } = this.context;
        return dataSet.delete(dataSet.selected);
    }
    handleButtonRemove() {
        const { dataSet, } = this.context;
        dataSet.remove(dataSet.selected);
    }
    handleButtonReset() {
        const { dataSet, } = this.context;
        dataSet.reset();
    }
    handleQueryReset() {
        const { dataSet: { queryDataSet }, } = this.context;
        if (queryDataSet) {
            const { current } = queryDataSet;
            if (current) {
                current.reset();
            }
            this.handleQuery();
        }
    }
    handleExpandAll() {
        const { tableStore } = this.context;
        tableStore.expandAll();
    }
    handleCollapseAll() {
        const { tableStore } = this.context;
        tableStore.collapseAll();
    }
    handleChangeExportStrategy(value) {
        const { dataSet } = this.context;
        dataSet.setState('__EXPORT-STRATEGY__', value);
    }
    async handleButtonExport() {
        const { tableStore, prefixCls, tableStore: { getConfig } } = this.context;
        const columnHeaders = await tableStore.getColumnHeaders();
        this.exportDataSet = new DataSet({ data: columnHeaders, paging: false }, { getConfig: getConfig });
        this.exportDataSet.selectAll();
        this.handleChangeExportStrategy('ALL');
        this.exportModal = Modal.open({
            title: $l('Table', 'choose_export_columns'),
            children: (React.createElement(React.Fragment, null,
                React.createElement(Table, { dataSet: this.exportDataSet, style: { height: pxToRem(300) } },
                    React.createElement(Column, { header: $l('Table', 'column_name'), name: "label", resizable: false })))),
            closable: true,
            okText: $l('Table', 'export_button'),
            onOk: this.handleExport,
            footer: (okBtn, cancelBtn) => (React.createElement("div", { className: `${prefixCls}-export-modal-footer` },
                React.createElement("div", { className: `${prefixCls}-export-modal-footer-radio` },
                    React.createElement(Radio, { name: "exportStrategy", value: "ALL", defaultChecked: true, onChange: this.handleChangeExportStrategy }, $l('Table', 'export_all')),
                    React.createElement(Radio, { name: "exportStrategy", value: "SELECTED", onChange: this.handleChangeExportStrategy }, $l('Table', 'export_selected'))),
                React.createElement("div", null,
                    okBtn,
                    cancelBtn))),
            style: {
                width: pxToRem(500),
            },
        });
    }
    async handleQuery() {
        const { tableStore, dataSet, dataSet: { queryDataSet }, } = this.context;
        if (tableStore.queryBar === TableQueryBarType.filterBar) {
            if (await dataSet.modifiedCheck(undefined, dataSet, 'query') && queryDataSet && queryDataSet.current && await queryDataSet.current.validate()) {
                return dataSet.query();
            }
        }
        else {
            return dataSet.query();
        }
    }
    handleExport() {
        const { selected } = this.exportDataSet;
        const { props: { clientExportQuantity }, context: { prefixCls, dataSet, }, } = this;
        if (selected.length) {
            const { exportModal } = this;
            dataSet.export(selected.reduce((columns, record) => {
                let myName = record.get('name');
                const myField = dataSet.getField(myName);
                if (myField && myField.type === FieldType.object) {
                    const bindField = findBindFieldBy(myField, dataSet.fields, 'textField');
                    if (bindField) {
                        myName = bindField.name;
                    }
                }
                columns[myName] = record.get('label');
                return columns;
            }, {}), clientExportQuantity).then((exportData) => {
                this.exportData = exportData;
            });
            if (exportModal) {
                exportModal.update({
                    title: $l('Table', 'export_button'),
                    children: (React.createElement(ExportBody, { prefixCls: prefixCls, dataSet: dataSet })),
                    onCancel: this.handleExportButton,
                    footer: React.createElement(ExportFooter, { prefixCls: prefixCls, exportButton: this.handleExportButton, exportModal: exportModal, dataSet: dataSet }),
                });
            }
        }
        return false;
    }
    handleExportButton(data, filename) {
        const { dataSet, tableStore, } = this.context;
        if (data === DataSetExportStatus.success) {
            if (this.exportData) {
                exportExcel(this.exportData, filename, tableStore.getConfig('xlsx'));
                this.exportModal.close();
                this.exportData = null;
            }
        }
        else if (data === DataSetExportStatus.failed) {
            this.exportData = null;
            this.handleExport();
        }
        else {
            this.exportModal.close();
            this.exportData = null;
        }
        dataSet.exportStatus = undefined;
    }
    getButtonProps(type) {
        const { isTree, dataSet, } = this.context;
        const disabled = dataSet.status !== DataSetStatus.ready;
        switch (type) {
            case TableButtonType.add:
                return {
                    icon: 'playlist_add',
                    onClick: this.handleButtonCreate,
                    children: $l('Table', 'create_button'),
                    disabled: disabled || (dataSet.parent ? !dataSet.parent.current : false),
                };
            case TableButtonType.save:
                return {
                    icon: 'save',
                    onClick: this.handleButtonSubmit,
                    children: $l('Table', 'save_button'),
                    type: ButtonType.submit,
                    disabled,
                };
            case TableButtonType.delete:
                return {
                    icon: 'delete',
                    onClick: this.handleButtonDelete,
                    children: $l('Table', 'delete_button'),
                    disabled: disabled || dataSet.selected.length === 0,
                };
            case TableButtonType.remove:
                return {
                    icon: 'remove_circle',
                    onClick: this.handleButtonRemove,
                    children: $l('Table', 'remove_button'),
                    disabled: disabled || dataSet.selected.length === 0,
                };
            case TableButtonType.reset:
                return {
                    icon: 'undo',
                    onClick: this.handleButtonReset,
                    children: $l('Table', 'reset_button'),
                    type: ButtonType.reset,
                };
            case TableButtonType.query:
                return { icon: 'search', onClick: this.handleQuery, children: $l('Table', 'query_button') };
            case TableButtonType.export:
                return {
                    icon: 'export',
                    onClick: this.handleButtonExport,
                    children: $l('Table', 'export_button'),
                };
            case TableButtonType.expandAll:
                return isTree
                    ? {
                        icon: 'add_box',
                        onClick: this.handleExpandAll,
                        children: $l('Table', 'expand_button'),
                    }
                    : undefined;
            case TableButtonType.collapseAll:
                return isTree
                    ? {
                        icon: 'short_text',
                        onClick: this.handleCollapseAll,
                        children: $l('Table', 'collapse_button'),
                    }
                    : undefined;
            default:
        }
    }
    /**
     * 渲染表头汇总列
     * @param summary
     */
    renderSummary(summary) {
        if (summary) {
            const { length } = summary;
            if (length) {
                const { summaryBar } = this.props;
                if (summaryBar) {
                    const { length: summaryLength } = summaryBar;
                    const { props: { summaryFieldsLimit, summaryBarFieldWidth }, context: { tableStore, dataSet, prefixCls, }, } = this;
                    const { props: { queryBarProps } } = tableStore;
                    const tableQueryBarProps = { ...tableStore.getConfig('queryBarProps'), ...queryBarProps };
                    const summaryFieldsLimits = summaryFieldsLimit || (tableQueryBarProps && tableQueryBarProps.summaryFieldsLimit) || 3;
                    const fieldTypeArr = [FieldType.currency, FieldType.number];
                    return summary.reduce((list, summaryCol, index) => {
                        const hasSeparate = length > summaryFieldsLimits || index !== (summaryLength - 1);
                        if (isString(summaryCol)) {
                            const field = dataSet.getField(summaryCol);
                            if (field && fieldTypeArr.includes(field.get('type'))) {
                                const summaryValue = dataSet.reduce((sum, record) => {
                                    const n = record.get(summaryCol);
                                    if (isNumber(n)) {
                                        return sum + n;
                                    }
                                    return sum;
                                }, 0);
                                const name = field.get('name');
                                const label = field.get('label');
                                list.push(React.createElement("div", { key: name },
                                    React.createElement("div", { className: `${prefixCls}-summary-col`, style: { width: summaryBarFieldWidth } },
                                        React.createElement("div", { className: `${prefixCls}-summary-col-label`, title: String(label) },
                                            label,
                                            ":"),
                                        React.createElement("div", { className: `${prefixCls}-summary-col-value`, title: String(summaryValue) }, summaryValue)),
                                    hasSeparate && React.createElement("div", { className: `${prefixCls}-summary-col-separate` },
                                        React.createElement("div", null))));
                            }
                        }
                        else if (typeof summaryCol === 'function') {
                            const summaryObj = summaryCol({ summaryFieldsLimit: summaryFieldsLimits, summaryBarFieldWidth, dataSet });
                            list.push(React.createElement("div", { key: isString(summaryObj.label) ? summaryObj.label : '' },
                                React.createElement("div", { className: `${prefixCls}-summary-col`, style: { width: summaryBarFieldWidth } },
                                    React.createElement("div", { className: `${prefixCls}-summary-col-label`, title: isString(summaryObj.label) ? summaryObj.label : '' },
                                        summaryObj.label,
                                        ":"),
                                    React.createElement("div", { className: `${prefixCls}-summary-col-value`, title: isString(summaryObj.value) || isNumber(summaryObj.value) ? summaryObj.value.toString() : '' }, summaryObj.value)),
                                hasSeparate && React.createElement("div", { className: `${prefixCls}-summary-col-separate` },
                                    React.createElement("div", null))));
                        }
                        return list;
                    }, []);
                }
            }
        }
    }
    /**
     * 汇总条展开收起按钮
     * @param summary
     */
    getMoreSummaryButton(summary) {
        if (summary.length) {
            const { prefixCls } = this.context;
            return (React.createElement("div", { className: `${prefixCls}-summary-button-more` },
                React.createElement("a", { onClick: () => this.openMore(summary) },
                    $l('Table', 'more'),
                    this.moreSummary && this.moreSummary.length ? React.createElement(Icon, { type: 'expand_less' }) : React.createElement(Icon, { type: 'expand_more' }))));
        }
    }
    /**
     * 渲染汇总条
     */
    getSummaryBar() {
        const { props: { summaryBar, summaryFieldsLimit, }, context: { prefixCls, tableStore, }, } = this;
        const { props: { queryBarProps } } = tableStore;
        const tableQueryBarProps = { ...tableStore.getConfig('queryBarProps'), ...queryBarProps };
        const summaryFieldsLimits = summaryFieldsLimit || (tableQueryBarProps && tableQueryBarProps.summaryFieldsLimit) || 3;
        if (summaryBar) {
            const currentSummaryBar = this.renderSummary(summaryBar.slice(0, summaryFieldsLimits));
            const moreSummary = summaryBar.slice(summaryFieldsLimits);
            const moreSummaryButton = this.getMoreSummaryButton(moreSummary);
            return (React.createElement("div", { className: `${prefixCls}-summary-group-wrapper` },
                React.createElement("div", { className: `${prefixCls}-summary-group` },
                    currentSummaryBar,
                    this.moreSummary),
                moreSummaryButton));
        }
    }
    /**
     * buttons 大于 buttonsLimits 放入下拉
     * @param buttonsLimits
     */
    getMoreButton(buttonsLimits) {
        const { buttons } = this.props;
        const { prefixCls, tableStore } = this.context;
        const tableButtonProps = tableStore.getConfig('tableButtonProps');
        const children = [];
        if (buttons && buttons.length && buttonsLimits) {
            buttons.slice(buttonsLimits).forEach(button => {
                let props = {};
                if (isArrayLike(button)) {
                    props = button[1] || {};
                    button = button[0];
                }
                if (isString(button) && button in TableButtonType) {
                    const { afterClick, ...buttonProps } = props;
                    const defaultButtonProps = this.getButtonProps(button);
                    if (defaultButtonProps) {
                        if (afterClick) {
                            const { onClick } = defaultButtonProps;
                            defaultButtonProps.onClick = async (e) => {
                                e.persist();
                                try {
                                    await onClick(e);
                                }
                                finally {
                                    afterClick(e);
                                }
                            };
                        }
                        children.push(React.createElement(Menu.Item, { key: button, className: `${prefixCls}-button-menu-item` },
                            React.createElement(Button, Object.assign({ key: `${button}-btn` }, tableButtonProps, defaultButtonProps, buttonProps))));
                    }
                }
                else if (isValidElement(button)) {
                    children.push(React.createElement(Menu.Item, { className: `${prefixCls}-button-menu-item` }, cloneElement(button, { ...tableButtonProps, ...button.props })));
                }
                else if (isObject(button)) {
                    children.push(React.createElement(Menu.Item, { className: `${prefixCls}-button-menu-item` },
                        React.createElement(Button, Object.assign({}, tableButtonProps, button))));
                }
            });
        }
        const menu = (React.createElement(Menu, null, children));
        return (React.createElement(Dropdown, { overlay: menu, key: "dropdown_button" },
            React.createElement(Button, Object.assign({}, tableButtonProps, { key: "more_button" }),
                React.createElement("span", null, $l('Table', 'more')),
                React.createElement(Icon, { type: 'expand_more' }))));
    }
    getButtons() {
        const { buttons, summaryBar, buttonsLimit } = this.props;
        const { tableStore } = this.context;
        const children = [];
        if (buttons) {
            // 汇总条存在下 buttons 大于 3 个放入下拉
            const buttonsLimits = summaryBar ? (buttonsLimit || 3) : buttonsLimit;
            const tableButtonProps = tableStore.getConfig('tableButtonProps');
            const buttonsArr = buttons.slice(0, buttonsLimits);
            buttonsArr.forEach(button => {
                let props = {};
                if (isArrayLike(button)) {
                    props = button[1] || {};
                    button = button[0];
                }
                if (isString(button) && button in TableButtonType) {
                    const { afterClick, ...buttonProps } = props;
                    const defaultButtonProps = this.getButtonProps(button);
                    if (defaultButtonProps) {
                        if (afterClick) {
                            const { onClick } = defaultButtonProps;
                            defaultButtonProps.onClick = async (e) => {
                                e.persist();
                                try {
                                    await onClick(e);
                                }
                                finally {
                                    afterClick(e);
                                }
                            };
                        }
                        children.push(React.createElement(Button, Object.assign({ key: button }, tableButtonProps, defaultButtonProps, buttonProps)));
                    }
                }
                else if (isValidElement(button)) {
                    children.push(cloneElement(button, { ...tableButtonProps, ...button.props }));
                }
                else if (isObject(button)) {
                    children.push(React.createElement(Button, Object.assign({}, tableButtonProps, button)));
                }
            });
            if (buttonsLimits && buttons.length > buttonsLimits) {
                const moreButton = this.getMoreButton(buttonsLimits);
                children.push(moreButton);
            }
        }
        return children;
    }
    getQueryFields() {
        const { context: { dataSet, tableStore: { queryBar }, }, props: { queryFields }, } = this;
        const { queryDataSet } = dataSet;
        const result = [];
        if (queryDataSet) {
            const { fields, current, props: { fields: propFields = [] } } = queryDataSet;
            const cloneFields = fields.toJS();
            const processField = (field, name) => {
                if (!field.get('bind', current) && !name.includes('__tls')) {
                    const element = queryFields[name];
                    let filterBarProps = {};
                    if (queryBar === TableQueryBarType.filterBar) {
                        const placeholder = isValidElement(element) && element.props.placeholder ? element.props.placeholder : getPlaceholderByField(field, current);
                        filterBarProps = {
                            placeholder,
                            border: false,
                            clearButton: true,
                        };
                    }
                    const props = {
                        key: name,
                        name,
                        dataSet: queryDataSet,
                        isFlat: queryBar === TableQueryBarType.filterBar,
                        ...filterBarProps,
                    };
                    result.push(isValidElement(element)
                        ? cloneElement(element, props)
                        : cloneElement(getEditorByField(field, current, queryBar !== TableQueryBarType.professionalBar, queryBar === TableQueryBarType.filterBar || queryBar === TableQueryBarType.comboBar), {
                            ...props,
                            ...(isObject(element) ? element : {}),
                        }));
                }
            };
            propFields.forEach(({ name }) => {
                if (name) {
                    const field = cloneFields.get(name);
                    if (field) {
                        cloneFields.delete(name);
                        processField(field, name);
                    }
                }
            });
            cloneFields.forEach((field, name) => {
                processField(field, name);
            });
        }
        return result;
    }
    renderToolBar(props) {
        const { prefixCls } = this.context;
        return React.createElement(TableToolBar, Object.assign({ key: "toolbar", prefixCls: prefixCls }, props));
    }
    renderFilterBar(props) {
        const { prefixCls } = this.context;
        const { props: { filterBarFieldName, filterBarPlaceholder }, } = this;
        return (React.createElement(TableFilterBar, Object.assign({ key: "toolbar", prefixCls: prefixCls, paramName: filterBarFieldName, placeholder: filterBarPlaceholder }, props)));
    }
    renderAdvancedQueryBar(props) {
        const { prefixCls } = this.context;
        return React.createElement(TableAdvancedQueryBar, Object.assign({ key: "toolbar", prefixCls: prefixCls }, props));
    }
    renderProfessionalBar(props) {
        const { prefixCls } = this.context;
        return React.createElement(TableProfessionalBar, Object.assign({ key: "toolbar", prefixCls: prefixCls }, props));
    }
    renderDynamicFilterBar(props) {
        const { dynamicFilterBar, searchCode } = this.props;
        const { prefixCls } = this.context;
        return React.createElement(TableDynamicFilterBar, Object.assign({ key: "toolbar", searchCode: searchCode, dynamicFilterBar: dynamicFilterBar, prefixCls: prefixCls }, props));
    }
    renderComboBar(props) {
        const { comboFilterBar } = this.props;
        const { prefixCls } = this.context;
        return React.createElement(TableComboBar, Object.assign({ key: "toolbar", comboFilterBar: comboFilterBar, prefixCls: prefixCls }, props));
    }
    expandTree() {
        const { tableStore } = this.context;
        const { props: { queryBarProps } } = tableStore;
        if (queryBarProps && typeof queryBarProps.onQuery === 'function') {
            queryBarProps.onQuery();
        }
        tableStore.expandAll();
    }
    collapseTree() {
        const { tableStore } = this.context;
        const { props: { queryBarProps } } = tableStore;
        if (queryBarProps && typeof queryBarProps.onReset === 'function') {
            queryBarProps.onReset();
        }
        tableStore.collapseAll();
    }
    render() {
        const buttons = this.getButtons();
        const summaryBar = this.getSummaryBar();
        const { context: { prefixCls, dataSet, isTree, tableStore, tableStore: { queryBar, props: { queryBarProps } }, }, props: { queryFieldsLimit, summaryFieldsLimit, pagination, treeQueryExpanded }, showQueryBar, } = this;
        if (showQueryBar) {
            const { queryDataSet } = dataSet;
            const queryFields = this.getQueryFields();
            const tableQueryBarProps = { ...tableStore.getConfig('queryBarProps'), ...queryBarProps };
            const onReset = typeof tableQueryBarProps.onReset === 'function' ? tableQueryBarProps.onReset : noop;
            const onQuery = typeof tableQueryBarProps.onQuery === 'function' ? tableQueryBarProps.onQuery : noop;
            const queryFieldsLimits = queryFieldsLimit || tableQueryBarProps.queryFieldsLimit;
            const summaryFieldsLimits = summaryFieldsLimit || tableQueryBarProps.summaryFieldsLimit || 3;
            const props = {
                ...tableQueryBarProps,
                dataSet,
                queryDataSet,
                buttons,
                pagination,
                queryFields,
                queryFieldsLimit: queryFieldsLimits,
                summaryFieldsLimit: summaryFieldsLimits,
                summaryBar,
                onQuery: treeQueryExpanded && isTree ? this.expandTree : onQuery,
                onReset: treeQueryExpanded && isTree ? this.collapseTree : onReset,
            };
            if (typeof queryBar === 'function') {
                return queryBar(props);
            }
            switch (queryBar) {
                case TableQueryBarType.normal:
                    return this.renderToolBar(props);
                case TableQueryBarType.bar:
                    return this.renderFilterBar(props);
                case TableQueryBarType.advancedBar:
                    return this.renderAdvancedQueryBar(props);
                case TableQueryBarType.professionalBar:
                    return this.renderProfessionalBar(props);
                case TableQueryBarType.filterBar:
                    return this.renderDynamicFilterBar(props);
                case TableQueryBarType.comboBar:
                    return this.renderComboBar(props);
                default:
            }
        }
        return [
            React.createElement(TableButtons, { key: "toolbar", prefixCls: prefixCls, buttons: buttons }, summaryBar),
            pagination,
        ];
    }
};
TableQueryBar.displayName = 'TableQueryBar';
TableQueryBar.defaultProps = {
    summaryBarFieldWidth: 170,
};
__decorate([
    observable
], TableQueryBar.prototype, "moreSummary", void 0);
__decorate([
    autobind
], TableQueryBar.prototype, "handleButtonCreate", null);
__decorate([
    autobind
], TableQueryBar.prototype, "handleButtonSubmit", null);
__decorate([
    autobind
], TableQueryBar.prototype, "handleButtonDelete", null);
__decorate([
    autobind
], TableQueryBar.prototype, "handleButtonRemove", null);
__decorate([
    autobind
], TableQueryBar.prototype, "handleButtonReset", null);
__decorate([
    autobind
], TableQueryBar.prototype, "handleQueryReset", null);
__decorate([
    autobind
], TableQueryBar.prototype, "handleExpandAll", null);
__decorate([
    autobind
], TableQueryBar.prototype, "handleCollapseAll", null);
__decorate([
    autobind
], TableQueryBar.prototype, "handleChangeExportStrategy", null);
__decorate([
    autobind
], TableQueryBar.prototype, "handleButtonExport", null);
__decorate([
    autobind
], TableQueryBar.prototype, "handleQuery", null);
__decorate([
    autobind
], TableQueryBar.prototype, "handleExport", null);
__decorate([
    autobind,
    action
], TableQueryBar.prototype, "handleExportButton", null);
__decorate([
    action
], TableQueryBar.prototype, "openMore", void 0);
__decorate([
    autobind
], TableQueryBar.prototype, "expandTree", null);
__decorate([
    autobind
], TableQueryBar.prototype, "collapseTree", null);
TableQueryBar = __decorate([
    observer
], TableQueryBar);
export default TableQueryBar;
